import React, { useEffect, useState } from 'react';
import './CameraData.css';
import PortalHeader from '../../../client-dashboard/clientNavbar/PortalHeader';
import FilterDropdown from '../../../team-portal/TeamPortalUtil/FilterDropdown';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { FaPlayCircle, FaFileArchive } from "react-icons/fa";

// ---------- Types ----------
interface FileEntry {
    name: string;
    path: string;
    type: 'Video' | 'Archive';
    date: string;
}

// ---------- Component ----------
const CameraData = () => {
    const [files, setFiles] = useState<FileEntry[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

    const [searchQuery, setSearchQuery] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<FileEntry["type"][]>([]);

    // ---------- Recursive fetch ----------
    const fetchAllFiles = async (path = ''): Promise<FileEntry[]> => {
        try {
            const res = await fetch(
                `/portal/api/camera/list?path=${encodeURIComponent(path)}`,
                { credentials: "same-origin" }
            );

            if (!res.ok) throw new Error('Failed');

            const data = await res.json();

            let results: FileEntry[] = [];

            for (const entry of data.entries) {
                const fullPath = path ? `${path}/${entry.name}` : entry.name;

                if (entry.is_dir) {
                    const sub = await fetchAllFiles(fullPath);
                    results = [...results, ...sub];
                } else {
                    const lower = entry.name.toLowerCase();

                    const type = lower.endsWith('.mp4') ? 'Video' : 'Archive';

                    // Normalize spaces → '0' to handle filenames like "Y202507 3H..." → "Y2025073H..."
                    const normalizedName = entry.name.replace(/ /g, '0');

                    let date = '';

                    // 1. Standard ISO format: 2025-05-21
                    const isoMatch = normalizedName.match(/(\d{4}-\d{2}-\d{2})/);

                    if (isoMatch) {
                        date = isoMatch[1];
                    }
                    // 2. Archive format: Y20250816H005625 → 2025-08-16
                    else {
                        const archiveMatch = normalizedName.match(/Y(\d{4})(\d{2})(\d{2})/);
                        if (archiveMatch) {
                            const [, y, m, d] = archiveMatch;
                            date = `${y}-${m}-${d}`;
                        }
                    }

                    results.push({
                        name: entry.name,  // keep original name for display
                        path: fullPath,
                        type,
                        date
                    });
                }
            }

            return results;

        } catch (err) {
            console.error(err);
            toast.error('Error loading files');
            return [];
        }
    };

    useEffect(() => {
        const load = async () => {
            const all = await fetchAllFiles('');
            all.sort((a, b) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            setFiles(all);
        };

        load();
    }, []);

    // ---------- Selection ----------
    const toggleFile = (path: string) => {
        setSelectedFiles(prev => {
            const next = new Set(prev);
            next.has(path) ? next.delete(path) : next.add(path);
            return next;
        });
    };

    // ---------- Download ----------
    const downloadFile = async (file: FileEntry) => {
        try {
            const res = await fetch(
                `/portal/api/camera/download?path=${encodeURIComponent(file.path)}`,
                { credentials: "same-origin" }
            );

            if (!res.ok) throw new Error();

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            a.click();

            URL.revokeObjectURL(url);
        } catch {
            toast.error(`Failed to download ${file.name}`);
        }
    };

    // ---------- Filtering ----------
    const filteredFiles = files.filter(f => {
        const matchesSearch =
            searchQuery === '' ||
            f.name.toLowerCase().includes(searchQuery.toLowerCase());

        const fileTime = f.date ? new Date(f.date).getTime() : 0;
        const start = startDate ? new Date(startDate).getTime() : null;
        const end = endDate ? new Date(endDate).getTime() : null;

        const matchesDate =
            (!start || fileTime >= start) &&
            (!end || fileTime <= end);

        const matchesType =
            selectedTypes.length === 0 || selectedTypes.includes(f.type);

        return matchesSearch && matchesDate && matchesType;
    });

    return (
        <div className="overall-div-for-suite">
            <ToastContainer />
            <PortalHeader />

            <div className="camera-wrapper">
                <div className="customer-title-and-expand-buttons">
                    <h1 className="cutomer-title">Camera Files</h1>
                </div>

                {/* Controls */}
                <div className="hardware3-action-buttons">

                    <div className='camera-filters'>
                        <input
                            type="text"
                            placeholder="Search files..."
                            className="hardware3-open-popup-button"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />

                        <FilterDropdown
                            dropdownClassName="camera-dropdown"
                            sideClass="detector-side-drop"
                            initialSelected={{ type: [] }}
                            resetValue={{ type: [] }}
                            sections={[
                                {
                                    key: "type",
                                    title: "File Type",
                                    options: ["Video", "Archive"],
                                    type: "checkbox",
                                },
                                {
                                    key: "date",
                                    title: "Date Range",
                                    options: [], // not used for date type
                                    type: "date",
                                },
                            ]}
                            onChange={(filters) => {
                                const validTypes: FileEntry["type"][] = (filters.type || []).filter(
                                    (t): t is FileEntry["type"] => t === "Video" || t === "Archive"
                                );

                                setSelectedTypes(validTypes);

                                // 👇 extract date range from filter state
                                const dateFilter = filters.date?.[0]
                                    ? JSON.parse(filters.date[0])
                                    : null;

                                setStartDate(dateFilter?.start || '');
                                setEndDate(dateFilter?.end || '');
                            }}
                        />
                    </div>

                    {/* Bulk Actions */}
                    <select
                        className="hardware3-open-popup-button hardware3-filter-select camera-select"
                        onChange={(e) => {
                            if (e.target.value === 'download') {
                                const selected = files.filter(f => selectedFiles.has(f.path));
                                selected.forEach(downloadFile);
                            }
                            e.target.value = '';
                        }}
                    >
                        <option value="" disabled selected>Actions</option>
                        <option value="download">Download</option>
                    </select>
                </div>

                {/* Table */}
                <div className="hardware3-table-wrapper camera-table-wrapper">
                    <table className="hardware3-table toa-efficiency-table">
                        <thead>
                            <tr>
                                <th colSpan={2}>
                                    <div className="toa-efficiency-cell">
                                        <input
                                            type="checkbox"
                                            checked={
                                                filteredFiles.length > 0 &&
                                                filteredFiles.every(f => selectedFiles.has(f.path))
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedFiles(new Set(filteredFiles.map(f => f.path)));
                                                } else {
                                                    setSelectedFiles(new Set());
                                                }
                                            }}
                                        />
                                        <div>File</div>
                                    </div>
                                </th>
                                <th>Type</th>
                                <th>Date</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredFiles.map(file => (
                                <tr key={file.path}>
                                    <td colSpan={2}>
                                        <div className="toa-efficiency-cell">
                                            <input
                                                type="checkbox"
                                                checked={selectedFiles.has(file.path)}
                                                onChange={() => toggleFile(file.path)}
                                            />
                                            <div>{file.name}</div>
                                        </div>
                                    </td>

                                    <td>
                                        <div className="toa-efficiency-cell">
                                            {file.type === 'Video' && <FaPlayCircle className='camera-type-icon'/>}
                                            {file.type === 'Archive' && <FaFileArchive className='camera-type-icon'/>}
                                            <div>{file.type}</div>
                                        </div>
                                    </td>

                                    <td>{file.date || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CameraData;